import { env } from '@/shared/config/env'
import type { HttpPost } from '@/shared/infra/http'

export const TELEMETRY_FLUSH_INTERVAL_MS = 60_000
export const TELEMETRY_MAX_BATCH_SIZE = 50

const ANALYTICS_EVENTS_PATH = '/analytics/events'
const CLIENT_PLATFORM = 'cc_web'

export type TelemetryEvent = Record<string, unknown>

let pendingEvents: TelemetryEvent[] = []
let activeHttpClient: HttpPost | null = null

export function enqueueTelemetryEvent(event: TelemetryEvent, httpClient: HttpPost): void {
  activeHttpClient = httpClient
  pendingEvents.push(event)
  if (pendingEvents.length >= TELEMETRY_MAX_BATCH_SIZE) {
    void flushTelemetryQueue()
  }
}

export function telemetryQueueSize(): number {
  return pendingEvents.length
}

export async function flushTelemetryQueue(): Promise<void> {
  if (pendingEvents.length === 0) return
  const httpClient = activeHttpClient
  if (!httpClient) return
  const events = pendingEvents
  pendingEvents = []
  try {
    await httpClient.post({ url: '/analytics/events', body: { events } })
  } catch {
    return
  }
}

export function startTelemetryFlushTimer(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const timerId = window.setInterval(() => {
    void flushTelemetryQueue()
  }, TELEMETRY_FLUSH_INTERVAL_MS)
  return () => window.clearInterval(timerId)
}

type RegisterTelemetryPageHideFlushParams = {
  getAuthToken: () => Promise<string | null>
  baseUrl: string
}

export function registerTelemetryPageHideFlush(
  params: RegisterTelemetryPageHideFlushParams,
): () => void {
  if (typeof window === 'undefined') return () => undefined

  let cachedToken: string | null = null

  const primeToken = (): void => {
    void params
      .getAuthToken()
      .then((token) => {
        cachedToken = token
      })
      .catch(() => {
        cachedToken = null
      })
  }

  primeToken()
  const primeTimerId = window.setInterval(primeToken, TELEMETRY_FLUSH_INTERVAL_MS)
  const sendPending = (): void => {
    if (pendingEvents.length === 0) return
    const events = pendingEvents
    pendingEvents = []

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-BB-Client': CLIENT_PLATFORM,
      'X-BB-App-Version': env.appVersion,
    }
    if (cachedToken) {
      headers.Authorization = `Bearer ${cachedToken}`
    }

    void fetch(`${params.baseUrl.replace(/\/$/, '')}${ANALYTICS_EVENTS_PATH}`, {
      method: 'POST',
      keepalive: true,
      headers,
      body: JSON.stringify({ events }),
    }).catch(() => {})
  }

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      sendPending()
      return
    }
    primeToken()
  }
  const handlePageHide = (): void => {
    sendPending()
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pagehide', handlePageHide)

  return () => {
    window.removeEventListener('pagehide', handlePageHide)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.clearInterval(primeTimerId)
  }
}

export function resetTelemetryQueueForTesting(): void {
  pendingEvents = []
  activeHttpClient = null
}
